import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { translate } from 'i18n/i18n'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { buildScheduleData } from '../_utils/buildScheduleData'
import { parseAllZoneEvents } from '../_utils/parseZoneEvents'
import { useIrrigationZoneNameDictionary } from '../_utils/useIrrigationZoneNameDictionary'
import type { TIcsDashboardData, TIrrigationPlanData } from '../types'
import { hasScheduleChanges } from './_utils/hasScheduleChanges'
import { updateScheduleDuration } from './_utils/updateScheduleDuration'
import { useEmitterVisibility } from './_utils/useEmitterVisibility'
import { useIrrigationEditPermissions } from './_utils/useIrrigationEditPermissions'
import { EmitterVisibilityToggles } from './EmitterVisibilityToggles'
import { IrrigationScheduleTable } from './IrrigationScheduleTable'
import { saveIrrigationSchedules } from './saveIrrigationSchedules'
import { ScheduleEditButtons } from './ScheduleEditButtons'

export const IrrigationPlannedNextWeek = ({
  eventsResponse,
  nextWeekDates,
  refetch,
}: {
  eventsResponse: TIcsDashboardData['eventsResponse']
  nextWeekDates: TIcsDashboardData['nextWeekDates']
  refetch: () => void
}) => {
  const [saveInProgress, setSaveInProgress] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [scheduleData, setScheduleData] = useState<TIrrigationPlanData[]>([])
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const irrigationZoneEmitters = selectedProperty?.irrigationZoneEmitters ?? {}

  const parsedEvents = useMemo(
    () => (selectedProperty?.timezone ? parseAllZoneEvents(eventsResponse, selectedProperty.timezone) : {}),
    [eventsResponse, selectedProperty?.timezone],
  )

  const initialScheduleData = useMemo(
    () =>
      selectedProperty?.timezone
        ? buildScheduleData(
            eventsResponse,
            irrigationZoneEmitters,
            nextWeekDates,
            selectedProperty.timezone,
            parsedEvents,
          )
        : [],
    [eventsResponse, irrigationZoneEmitters, nextWeekDates, selectedProperty?.timezone, parsedEvents],
  )

  const emitterTypes = useMemo(
    () => initialScheduleData.map((emitter) => emitter.emitterType),
    [initialScheduleData],
  )

  const { visibleEmitters, toggleEmitterVisibility } = useEmitterVisibility(emitterTypes)

  const hasChanges = useMemo(
    () => hasScheduleChanges(initialScheduleData, scheduleData),
    [initialScheduleData, scheduleData],
  )

  const { canEdit, canEditIcsDashboard, hasControlProvider } = useIrrigationEditPermissions({
    selectedPropertyId,
    irrigationZoneEmitters,
  })

  useEffect(() => {
    setScheduleData(initialScheduleData)

    setIsEditMode(false)
  }, [initialScheduleData])

  const irrigationZoneNameDictionary = useIrrigationZoneNameDictionary(irrigationZoneEmitters)

  const handleDurationChange = (
    emitterType: TEmitterType,
    zoneId: number,
    date: string,
    newDuration: number,
  ) => {
    setScheduleData((prev) => updateScheduleDuration(prev, emitterType, zoneId, date, newDuration))
  }

  const handleSaveAndFinish = async () => {
    if (!hasChanges) {
      setIsEditMode(false)

      return
    }

    setSaveInProgress(true)

    const success = await saveIrrigationSchedules(scheduleData, initialScheduleData)

    setSaveInProgress(false)

    if (success) {
      setIsEditMode(false)

      refetch()
    }
  }

  const handleCancelEdit = () => {
    setScheduleData(initialScheduleData)

    setIsEditMode(false)
  }

  if (!selectedProperty?.timezone) return null

  const { timezone } = selectedProperty

  return (
    <div>
      <div>
        <h3>{translate.phrases.banyanApp('Irrigation Scheduler (Hours)')}</h3>
        <div>
          <div css={{ marginTop: 16, marginBottom: 16 }}>
            <EmitterVisibilityToggles
              emitterTypes={emitterTypes}
              visibleEmitters={visibleEmitters}
              onToggle={toggleEmitterVisibility}
            />
          </div>
          {canEdit && (
            <ScheduleEditButtons
              isEditMode={isEditMode}
              hasChanges={hasChanges}
              saveInProgress={saveInProgress}
              onEditClick={() => setIsEditMode(true)}
              onCancelClick={handleCancelEdit}
              onSaveClick={handleSaveAndFinish}
            />
          )}
          {canEditIcsDashboard && hasControlProvider && (
            <div
              css={{
                marginTop: 16,
                padding: 10,
                backgroundColor: colors.deficitWarning,
                border: `1px solid ${colors.yellow}`,
                fontSize: 14,
                color: colors.yellow800,
              }}
            >
              {translate.phrases.banyanApp(
                'Editing is disabled because this property has irrigation zones with 3rd-party control providers.',
              )}
            </div>
          )}
        </div>
      </div>

      {scheduleData.map((emitter) => (
        <div
          key={emitter.emitterType}
          css={{ marginBottom: 32, display: visibleEmitters[emitter.emitterType] ? 'block' : 'none' }}
        >
          <IrrigationScheduleTable
            emitterData={emitter}
            irrigationZoneEmitters={irrigationZoneEmitters}
            irrigationZoneNameDictionary={irrigationZoneNameDictionary}
            timezone={timezone}
            isEditMode={isEditMode}
            onDurationChange={handleDurationChange}
          />
        </div>
      ))}
    </div>
  )
}
